import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { FaExclamationTriangle, FaSync, FaShieldAlt, FaCloud, FaArrowRight, FaNetworkWired, FaDesktop, FaCog, FaKey, FaSearch, FaChartLine, FaServer, FaMobileAlt, FaBrain, FaLink, FaShieldVirus } from 'react-icons/fa';
import '../../../styles/SOCOverviewStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * SOC Overview Dashboard - Unified Security Operations Center
 * Provides 360-degree security visibility across all platforms:
 * - Cloud: Microsoft Defender, Intune, Entra ID
 * - Network: MikroTik routers (router monitoring)
 * - On-Premise: Tactical RMM endpoints
 * - Analytics: Risk scoring, correlation rules, behavioral analytics
 */
const SOCOverviewDashboard = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [threatHuntingSummary, setThreatHuntingSummary] = useState(null);
    const [networkSecurity, setNetworkSecurity] = useState(null);
    const [endpointMetrics, setEndpointMetrics] = useState(null);
    const [enhancedMode, setEnhancedMode] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Helper function to get auth headers
    const getAuthHeaders = () => {
        const storedUser = user || JSON.parse(localStorage.getItem('user'));
        return storedUser?.token ? { Authorization: `Bearer ${storedUser.token}` } : {};
    };

    /**
     * Load threat hunting summary from Defender
     */
    const loadThreatHuntingSummary = async () => {
        try {
            const endpoint = enhancedMode 
                ? '/api/ThreatHunting/enhanced-summary'
                : '/api/ThreatHunting/summary';
            const response = await fetch(endpoint, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load threat hunting data');
            const data = await response.json();
            setThreatHuntingSummary(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading threat hunting summary:', err);
        }
    };

    /**
     * Load network security metrics from MikroTik
     */
    const loadNetworkSecurity = async () => {
        try {
            const response = await fetch('/api/NetworkSecurity/security/aggregated', { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load network security');
            const data = await response.json();
            setNetworkSecurity(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading network security:', err);
        }
    };

    /**
     * Load endpoint metrics from Tactical RMM
     */
    const loadEndpointMetrics = async () => {
        try {
            const response = await fetch('/api/TacticalRMM/metrics?daysBack=7', { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load endpoint metrics');
            const data = await response.json();
            setEndpointMetrics(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading endpoint metrics:', err);
        }
    };

    /**
     * Initial data load
     */
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    loadThreatHuntingSummary(),
                    loadNetworkSecurity(),
                    loadEndpointMetrics()
                ]);
                setError(null);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [enhancedMode]);

    /**
     * Refresh all data
     */
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await Promise.all([
                loadThreatHuntingSummary(),
                loadNetworkSecurity(),
                loadEndpointMetrics()
            ]);
            setError(null);
        } catch (catchError) {
            Sentry.captureException(catchError);
            setError('Failed to refresh data');
        } finally {
            setRefreshing(false);
        }
    };

    /**
     * Calculate overall security score
     */
    const calculateSecurityScore = () => {
        if (!threatHuntingSummary && !networkSecurity && !endpointMetrics) return 0;

        let score = 100;

        // Threat hunting incidents reduce score
        if (threatHuntingSummary) {
            score -= (threatHuntingSummary.dlpIncidents || 0) * 5;
            score -= (threatHuntingSummary.insiderRiskIncidents || 0) * 3;
        }

        // Network security events reduce score
        if (networkSecurity) {
            score -= (networkSecurity.highRiskRouters || 0) * 10;
            score -= Math.min((networkSecurity.portScansDetected || 0) * 2, 20);
        }

        // Endpoint issues reduce score
        if (endpointMetrics) {
            score -= (endpointMetrics.overdueAgents || 0) * 5;
            score -= (endpointMetrics.errorAlerts || 0) * 2;
        }

        return Math.max(0, Math.min(100, score));
    };

    /**
     * Get security score status
     */
    const getScoreStatus = (score) => {
        if (score >= 90) return { level: 'excellent', label: 'Excellent', color: '#28a745' };
        if (score >= 75) return { level: 'good', label: 'Good', color: '#17a2b8' };
        if (score >= 60) return { level: 'moderate', label: 'Moderate', color: '#ffc107' };
        if (score >= 40) return { level: 'concerning', label: 'Concerning', color: '#fd7e14' };
        return { level: 'critical', label: 'Critical', color: '#dc3545' };
    };

    if (loading) {
        return (
            <div className="soc-overview-dashboard">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading SOC overview...</p>
                </div>
            </div>
        );
    }

    const securityScore = calculateSecurityScore();
    const scoreStatus = getScoreStatus(securityScore);

    return (
        <div className="soc-overview-dashboard">
            <div className="dashboard-header">
                <h1>
                    <FaShieldAlt /> Security Operations Center
                </h1>
                <p className="subtitle">Unified Security Monitoring & Threat Intelligence</p>
                <div className="header-controls">
                    <label className="enhanced-toggle">
                        <input
                            type="checkbox"
                            checked={enhancedMode}
                            onChange={(e) => setEnhancedMode(e.target.checked)}
                        />
                        <span>Enhanced Mode (Graph APIs)</span>
                    </label>
                    <button 
                        onClick={handleRefresh} 
                        className="btn-refresh"
                        disabled={refreshing}
                    >
                        <FaSync className={refreshing ? 'spinning' : ''} />
                        {refreshing ? ' Refreshing...' : ' Refresh'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-banner">
                    <FaExclamationTriangle />
                    <span>{error}</span>
                </div>
            )}

            {/* Overall Security Score */}
            <div className="security-score-section">
                <h2>Overall Security Posture</h2>
                <div className="score-display">
                    <div className="score-circle" style={{ borderColor: scoreStatus.color }}>
                        <div className="score-value" style={{ color: scoreStatus.color }}>
                            {securityScore}
                        </div>
                        <div className="score-max">/100</div>
                    </div>
                    <div className="score-details">
                        <div className={`score-status status-${scoreStatus.level}`}>
                            {scoreStatus.label}
                        </div>
                        <p className="score-description">
                            Composite score based on threat hunting, network security, and endpoint health
                        </p>
                    </div>
                </div>
            </div>

            {/* Security Domains Grid */}
            <div className="security-domains">
                <h2>Security Monitoring Domains</h2>
                <div className="domains-grid">
                    {/* Cloud Security */}
                    <div className="domain-card">
                        <div className="domain-header">
                            <FaCloud />
                            <h3>Cloud Security</h3>
                        </div>
                        <div className="domain-content">
                            {threatHuntingSummary ? (
                                <>
                                    <div className="metric">
                                        <span className="metric-label">DLP Incidents:</span>
                                        <span className="metric-value">{threatHuntingSummary.dlpIncidents || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">Insider Risk:</span>
                                        <span className="metric-value">{threatHuntingSummary.insiderRiskIncidents || 0}</span>
                                    </div>
                                    {enhancedMode && threatHuntingSummary.intuneAlerts !== undefined && (
                                        <>
                                            <div className="metric">
                                                <span className="metric-label">Intune Alerts:</span>
                                                <span className="metric-value">{threatHuntingSummary.intuneAlerts}</span>
                                            </div>
                                            <div className="metric">
                                                <span className="metric-label">Entra ID Risks:</span>
                                                <span className="metric-value">{threatHuntingSummary.entraIdRisks || 0}</span>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <p className="no-data">Data unavailable</p>
                            )}
                        </div>
                        <Link to="/dashboards/threat-hunting" className="domain-link">
                            View Details <FaArrowRight />
                        </Link>
                    </div>

                    {/* Network Security */}
                    <div className="domain-card">
                        <div className="domain-header">
                            <FaNetworkWired />
                            <h3>Network Security</h3>
                        </div>
                        <div className="domain-content">
                            {networkSecurity ? (
                                <>
                                    <div className="metric">
                                        <span className="metric-label">Routers:</span>
                                        <span className="metric-value">{networkSecurity.totalRouters || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">High Risk:</span>
                                        <span className="metric-value warning">{networkSecurity.highRiskRouters || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">Port Scans:</span>
                                        <span className="metric-value">{networkSecurity.portScansDetected || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">Firewall Events:</span>
                                        <span className="metric-value">{networkSecurity.totalFirewallEvents || 0}</span>
                                    </div>
                                </>
                            ) : (
                                <p className="no-data">Data unavailable</p>
                            )}
                        </div>
                        <Link to="/dashboards/network-security" className="domain-link">
                            View Details <FaArrowRight />
                        </Link>
                    </div>

                    {/* Endpoint Security */}
                    <div className="domain-card">
                        <div className="domain-header">
                            <FaDesktop />
                            <h3>Endpoint Security</h3>
                        </div>
                        <div className="domain-content">
                            {endpointMetrics ? (
                                <>
                                    <div className="metric">
                                        <span className="metric-label">Total Endpoints:</span>
                                        <span className="metric-value">{endpointMetrics.totalAgents || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">Online:</span>
                                        <span className="metric-value success">{endpointMetrics.onlineAgents || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">Offline:</span>
                                        <span className="metric-value warning">{endpointMetrics.offlineAgents || 0}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metric-label">Error Alerts:</span>
                                        <span className="metric-value error">{endpointMetrics.errorAlerts || 0}</span>
                                    </div>
                                </>
                            ) : (
                                <p className="no-data">Data unavailable</p>
                            )}
                        </div>
                        <Link to="/dashboards/endpoint-security" className="domain-link">
                            View Details <FaArrowRight />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
                <h2>Quick Actions</h2>
                <div className="actions-grid">
                    <Link to="/settings/router-config" className="action-card">
                        <FaCog />
                        <span>Configure Routers</span>
                    </Link>
                    <Link to="/settings/secrets" className="action-card">
                        <FaKey />
                        <span>Manage Secrets</span>
                    </Link>
                    <Link to="/dashboards/threat-hunting" className="action-card">
                        <FaSearch />
                        <span>Threat Hunting</span>
                    </Link>
                    <Link to="/dashboards/itreport" className="action-card">
                        <FaChartLine />
                        <span>IT Reports</span>
                    </Link>
                </div>
            </div>

            {/* Security Features Overview */}
            <div className="features-section">
                <h2>SOC Platform Capabilities</h2>
                <div className="features-grid">
                    <div className="feature-item">
                        <FaShieldVirus />
                        <h3>Threat Hunting</h3>
                        <p>7 KQL-based queries for DLP and insider risk detection using Microsoft Defender Advanced Hunting</p>
                    </div>
                    <div className="feature-item">
                        <FaBrain />
                        <h3>Risk Scoring</h3>
                        <p>Composite risk calculation (0-100 scale) based on multiple security factors with time-based amplification</p>
                    </div>
                    <div className="feature-item">
                        <FaLink />
                        <h3>Correlation Engine</h3>
                        <p>Detects complex attack patterns across platforms with MITRE ATT&CK technique mapping</p>
                    </div>
                    <div className="feature-item">
                        <FaChartLine />
                        <h3>Behavioral Analytics</h3>
                        <p>Establishes baselines and detects anomalies in user and system behavior patterns</p>
                    </div>
                    <div className="feature-item">
                        <FaNetworkWired />
                        <h3>Network Security</h3>
                        <p>MikroTik router monitoring with firewall analysis, port scan detection, and VPN access logging</p>
                    </div>
                    <div className="feature-item">
                        <FaDesktop />
                        <h3>Endpoint Telemetry</h3>
                        <p>On-premise endpoint monitoring through Tactical RMM with alert aggregation and correlation</p>
                    </div>
                </div>
            </div>

            {/* Integration Status */}
            <div className="integration-status">
                <h2>Integration Status</h2>
                <div className="status-items">
                    <div className={`status-item ${threatHuntingSummary ? 'active' : 'inactive'}`}>
                        <FaShieldAlt />
                        <span>Microsoft Defender</span>
                        <div className={`status-badge ${threatHuntingSummary ? 'active' : 'inactive'}`}>
                            {threatHuntingSummary ? 'Active' : 'Inactive'}
                        </div>
                    </div>
                    <div className={`status-item ${networkSecurity ? 'active' : 'inactive'}`}>
                        {/* FaServer used as fallback since FaRouter is not available in react-icons/fa */}
                        <FaServer />
                        <span>MikroTik Routers</span>
                        <div className={`status-badge ${networkSecurity ? 'active' : 'inactive'}`}>
                            {networkSecurity ? 'Active' : 'Inactive'}
                        </div>
                    </div>
                    <div className={`status-item ${endpointMetrics ? 'active' : 'inactive'}`}>
                        <FaDesktop />
                        <span>Tactical RMM</span>
                        <div className={`status-badge ${endpointMetrics ? 'active' : 'inactive'}`}>
                            {endpointMetrics ? 'Active' : 'Inactive'}
                        </div>
                    </div>
                    {enhancedMode && (
                        <div className={`status-item ${threatHuntingSummary?.intuneAlerts !== undefined ? 'active' : 'inactive'}`}>
                            <FaMobileAlt />
                            <span>Intune / Entra ID</span>
                            <div className={`status-badge ${threatHuntingSummary?.intuneAlerts !== undefined ? 'active' : 'inactive'}`}>
                                {threatHuntingSummary?.intuneAlerts !== undefined ? 'Active' : 'Inactive'}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SOCOverviewDashboard;
